import { Box, Heading, HStack, Icon, Text } from "@chakra-ui/react";
import { Table } from "../../components/Table/Table";
import { useRule } from "./useRule";
import { MdEditOff, MdModeEdit, MdSave } from "react-icons/md";
import { FaCode } from "react-icons/fa6";
import { IoMdAdd } from "react-icons/io";
import { BrandButton } from "../../components/BrandButton/BrandButton";
import { Editor } from "@monaco-editor/react";
import Split from "react-split";

export const Rule = () => {
  const {
    editorOptions,
    selectedRule,
    selectedRuleBody,
    columns,
    columnData,
    testInputColumns,
    testInputColumnData,
    isEditing,
    isEdited,
    responseData,
    status,
    onEditToggle,
    onSave,
    onAddField,
    setInputBody,
    onTextInput,
  } = useRule();

  return (
    <Box
      bg="rgba(255, 255, 255, 0.05)"
      backdropFilter="blur(20px)"
      borderRadius="20px"
      border="1px solid rgba(255, 255, 255, 0.1)"
      p={6}
    >
      <Box borderRadius="md" py={3}>
        <HStack justifyContent="space-between">
          <HStack>
            <Icon
              as={FaCode as React.ElementType}
              color="orange.400"
              boxSize="24px"
            />
            <Heading fontSize="24px" color="white">
              {selectedRule?.ruleNm}
            </Heading>
          </HStack>
          <HStack>
            <Icon
              as={
                isEditing
                  ? (MdEditOff as React.ElementType)
                  : (MdModeEdit as React.ElementType)
              }
              onClick={onEditToggle}
              cursor="pointer"
              boxSize="20px"
              color="rgba(255, 255, 255, 0.8)"
              _hover={{ color: "brand.400" }}
            />
            {isEdited ? (
              <Icon
                as={MdSave as React.ElementType}
                cursor="pointer"
                boxSize="20px"
                onClick={onSave}
                color="green.400"
                _hover={{ color: "green.300" }}
              />
            ) : null}
          </HStack>
        </HStack>
      </Box>
      <Box
        as={Split}
        display="flex"
        flexDir="row"
        gutterStyle={() => ({
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "50%",
          cursor: "col-resize",
          width: "4px",
          borderRadius: "2px",
        })}
        gap={3}
      >
        <Box width="33%" borderRadius="md">
          <HStack justifyContent="space-between" p={3}>
            <Heading fontSize="22px" color="white">
              Input body
            </Heading>
          </HStack>
          <Editor
            defaultValue={selectedRuleBody}
            defaultLanguage="groovy"
            height="50vh"
            theme="vs-dark"
            options={editorOptions}
            onChange={(e) => setInputBody(e || "")}
            onMount={(editor, monaco) => {
              const container = editor.getContainerDomNode();

              // Use ResizeObserver to handle layout updates
              const resizeObserver = new ResizeObserver(() => {
                editor.layout();
              });

              resizeObserver.observe(container);

              // Cleanup observer on unmount
              return () => {
                resizeObserver.disconnect();
              };
            }}
          />
        </Box>
        <Box width="30%">
          <HStack justifyContent="space-between" p={3}>
            <Heading fontSize="22px" color="white">
              Test inputs
            </Heading>
          </HStack>
          <Table
            data={testInputColumnData}
            columns={testInputColumns}
            paginationEnabled={false}
          />
          <HStack justifyContent="flex-end" py={3}>
            <BrandButton onClick={onTextInput}>Test input</BrandButton>
          </HStack>
        </Box>
        <Box width="36%">
          <HStack justifyContent="space-between" p={3}>
            <Heading fontSize="22px" color="white">
              Parameters
            </Heading>
            {isEditing ? (
              <Icon
                as={IoMdAdd as React.ElementType}
                cursor="pointer"
                boxSize="20px"
                onClick={onAddField}
                color="rgba(255, 255, 255, 0.8)"
                _hover={{ color: "brand.400" }}
              />
            ) : null}
          </HStack>
          <Table
            data={columnData}
            columns={columns}
            paginationEnabled={false}
          />
        </Box>
      </Box>
      {status && (
        <Box
          bg="rgba(255, 255, 255, 0.05)"
          backdropFilter="blur(20px)"
          borderRadius="20px"
          border="1px solid rgba(255, 255, 255, 0.1)"
          p={4}
          mt={3}
        >
          <Heading fontSize="22px" color="white">
            Result
          </Heading>
          <Box
            bg="rgba(0, 0, 0, 0.3)"
            borderRadius="12px"
            border="1px solid rgba(255, 255, 255, 0.1)"
            p={4}
            mt={3}
          >
            <Text
              color={status === "success" ? "green.300" : "red.400"}
              fontFamily="mono"
              fontSize="sm"
              whiteSpace="pre-wrap"
            >
              {JSON.stringify(responseData, null, 2)}
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};
